import { Component, ChangeDetectionStrategy } from "@angular/core";
import {
  DataService,
  ModalService,
  NotificationService,
  SharedModule,
  TypedBaseListComponent,
} from "@bb-vendure/admin-ui/core";
import {
  CreateTicketDocument,
  FetchTicketsAssignedToMeDocument,
  TicketType,
} from "src/generated-gql/graphql";
import { take } from "rxjs";
import AssigneeComponent from "../../user-select/user-select.component";
import CreateTicketRequest from "../../Create/create.dialog";

@Component({
  standalone: true,
  imports: [SharedModule],
  selector: "internal-list",
  templateUrl: "./assign-to-me.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalAsgToMeComponent extends TypedBaseListComponent<
  typeof FetchTicketsAssignedToMeDocument,
  'ticketsAssignedToMe'
> {
  readonly sorts = this.createSortCollection()
    .defaultSort("updatedAt", "DESC")
    .connectToRoute(this.route);
  readonly filters = this.createFilterCollection()
    .addIdFilter()
    .addDateFilters()
    .addFilter({
      name: "ticket.assignee",
      label: "ticket.assignee",
      type: {
        kind: "custom",
        component: AssigneeComponent,
        serializeValue: (value) => {
          return `${value.id}|${value.firstName}|${value.lastName}`;
        },
        deserializeValue: (value) => {
          const [id, firstName, lastName] = value.split("|");
          return { id, firstName, lastName };
        },
        getLabel: (value) => {
          return value.firstName + " " + value.lastName;
        },
      },
      toFilterInput: (value) => ({
        assigneeId: {
          eq: value.id,
        },
      }),
    })
    .addFilter({
      name: "ticket.requester",
      label: "ticket.requester",
      type: {
        kind: "custom",
        component: AssigneeComponent,
        serializeValue: (value) => {
          return `${value.id}|${value.firstName}|${value.lastName}`;
        },
        deserializeValue: (value) => {
          const [id, firstName, lastName] = value.split("|");
          return { id, firstName, lastName };
        },
        getLabel: (value) => {
          return value.firstName + " " + value.lastName;
        },
      },
      toFilterInput: (value) => ({
        requesterId: {
          eq: value.id,
        },
      }),
    })
    .connectToRoute(this.route);

  constructor(
    protected dataService: DataService,
    private modalService: ModalService,
    private notiService: NotificationService
  ) {
    super();
    this.dataService.auth.currentUser().single$.subscribe();
    super.configure({
      document: FetchTicketsAssignedToMeDocument,
      getItems: (data) => data.ticketsAssignedToMe,
      setVariables: (skip, take) => {
        return {
          options: {
            skip,
            take,
            filter: {
              ...this.filters.createFilterInput(),
              type: {
                eq: TicketType.InternalTicket,
              },
            },
            sort: this.sorts.createSortInput(),
          },
        };
      },
      refreshListOnChanges: [
        this.filters.valueChanges,
        this.sorts.valueChanges,
      ],
    });
  }

  createInternalTicket() {
    this.modalService
      .fromComponent(CreateTicketRequest, {
        size: "xl",
        closable: true,
        locals: {
          isFromFloating: false,
          isInternal: true,
        },
      })
      .subscribe((result) => {
        if (result) {
          this.dataService
            .mutate(CreateTicketDocument, {
              input: result,
            })
            .pipe(take(1))
            .subscribe((result) => {
              if (result.createTicket) {
                this.notiService.success("ticket.create-success");
                this.router.navigate([
                  "extensions",
                  "tickets",
                  result.createTicket.id,
                ]);
              }
            });
        }
      });
  }

  getColorStatus(status: string) {
    switch (status) {
      case "New":
        return "success";
      case "Open":
        return "warning";
      case "Solved":
        return "error";
      case "Closed":
        return "success";
      case "Pending":
        return "warning";
      default:
        return "error";
    }
  }
}
